import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { applicationAPI } from '../services/api';
import { PageLayout, ConfirmDialog } from './ui';

const APP_TYPE_ICONS = {
  web: '🌐',
  spa: '📱',
  native: '📲',
  service: '⚙️',
  saml: '🔐',
  bookmark: '🔖'
};

const APP_TYPE_NAMES = {
  web: 'Web Application',
  spa: 'Single Page App',
  native: 'Native/Mobile',
  service: 'Service (M2M)',
  saml: 'SAML 2.0',
  bookmark: 'Bookmark'
};

const getAppTypeIcon = (appType) => APP_TYPE_ICONS[appType] || '📦';
const getAppTypeName = (appType) => APP_TYPE_NAMES[appType] || appType;

const ApplicationManager = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [showSecretModal, setShowSecretModal] = useState(false);
  const [newSecret, setNewSecret] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null, variant: 'danger' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const appsResponse = await applicationAPI.list();
      setApplications(appsResponse.applications || []);
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (appId) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Application',
      message: 'Are you sure you want to delete this application? This action cannot be undone.',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await applicationAPI.delete(appId);
          setSuccess('Application deleted successfully');
          loadData();
        } catch (err) {
          setError(err.response?.data?.error || err.message || 'Failed to delete application');
        } finally {
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, variant: 'danger' });
        }
      }
    });
  };

  const handleRotateSecret = async (appId) => {
    setConfirmDialog({
      open: true,
      title: 'Rotate Client Secret',
      message: 'Rotate client secret? The old secret will stop working immediately.',
      variant: 'warning',
      onConfirm: async () => {
        try {
          const response = await applicationAPI.rotateSecret(appId);
          setNewSecret(response.client_secret);
          setShowSecretModal(true);
        } catch (err) {
          setError(err.message || 'Failed to rotate secret');
        } finally {
          setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, variant: 'danger' });
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Loading applications...</div>
      </div>
    );
  }

  const backTo = location.state?.from || '/management';
  const backLabel = backTo === '/tasks' ? 'Back to Tasks' : 'Back to Management';

  return (
    <PageLayout
      title="Applications"
      subtitle="Manage SAML applications"
      backTo={backTo}
      backLabel={backLabel}
      actions={
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-hover text-sm font-medium"
        >
          + New Application
        </button>
      }
    >
      {/* Messages */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
          <button onClick={() => setError('')} className="float-right font-bold">×</button>
        </div>
      )}
      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {success}
          <button onClick={() => setSuccess('')} className="float-right font-bold">×</button>
        </div>
      )}

      {/* Applications Grid */}
      {applications.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <div className="text-6xl mb-4">📱</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No applications yet</h3>
          <p className="text-gray-600 mb-4">Create your first SAML application</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-primary text-white px-6 py-2 rounded-md hover:bg-primary-hover"
          >
            Create Application
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {applications.map(app => (
            <div key={app.id} className="bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <div className="p-6">
                {/* App Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    {app.logo_url ? (
                      <img src={app.logo_url.startsWith('/') ? `${process.env.REACT_APP_API_URL || ''}${app.logo_url}` : app.logo_url} alt={app.label} className="w-12 h-12 rounded mr-3 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div className="w-12 h-12 bg-primary-light rounded flex items-center justify-center text-2xl mr-3">
                        {getAppTypeIcon(app.app_type)}
                      </div>
                    )}
                    <div>
                      <h3 className="font-semibold text-gray-900">{app.label}</h3>
                      <p className="text-sm text-gray-500">{getAppTypeName(app.app_type)}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded ${
                    app.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {app.status}
                  </span>
                </div>

                {/* App Details */}
                {app.description && (
                  <p className="text-sm text-gray-600 mb-3">{app.description}</p>
                )}

                {/* Client ID */}
                {app.client_id && (
                  <div className="mb-3">
                    <label className="text-xs text-gray-500 block mb-1">Client ID</label>
                    <div className="flex items-center">
                      <code className="text-xs bg-gray-50 px-2 py-1 rounded flex-1 overflow-hidden text-ellipsis">
                        {app.client_id.substring(0, 20)}...
                      </code>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(app.client_id);
                          setSuccess('Client ID copied!');
                          setTimeout(() => setSuccess(''), 2000);
                        }}
                        className="ml-2 text-primary hover:text-indigo-800"
                        title="Copy Client ID"
                      >
                        📋
                      </button>
                    </div>
                  </div>
                )}

                {/* Scopes */}
                {app.scopes && app.scopes.length > 0 && (
                  <div className="mb-3">
                    <label className="text-xs text-gray-500 block mb-1">Scopes</label>
                    <div className="flex flex-wrap gap-1">
                      {app.scopes.slice(0, 3).map(scope => (
                        <span key={scope} className="text-xs bg-blue-50 text-primary px-2 py-1 rounded">
                          {scope}
                        </span>
                      ))}
                      {app.scopes.length > 3 && (
                        <span className="text-xs text-gray-500">+{app.scopes.length - 3} more</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 mt-4 pt-4 border-t">
                  <button
                    onClick={() => navigate(`/applications/${app.id}`)}
                    className="flex-1 text-sm text-primary hover:text-indigo-800 font-medium"
                  >
                    Manage
                  </button>
                  {app.app_type !== 'bookmark' && (
                    <button
                      onClick={() => handleRotateSecret(app.id)}
                      className="text-sm text-gray-600 hover:text-gray-800"
                      title="Rotate Secret"
                    >
                      🔄
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(app.id)}
                    className="text-sm text-red-600 hover:text-red-800"
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Application Modal */}
      {showCreateModal && (
        <CreateApplicationModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            setSuccess('Application created successfully!');
            loadData();
          }}
          onError={(err) => setError(err)}
        />
      )}

      {/* App Details Modal */}
      {selectedApp && (
        <AppDetailsModal
          app={selectedApp}
          onClose={() => setSelectedApp(null)}
          onUpdate={() => {
            setSelectedApp(null);
            loadData();
          }}
        />
      )}

      {/* Secret Modal */}
      {showSecretModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">⚠️ New Client Secret</h3>
            <p className="text-sm text-gray-600 mb-4">
              Save this secret now - it will not be shown again!
            </p>
            <div className="bg-gray-50 p-3 rounded mb-4">
              <code className="text-sm break-all">{newSecret}</code>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(newSecret);
                  setSuccess('Secret copied to clipboard!');
                }}
                className="flex-1 bg-primary text-white px-4 py-2 rounded hover:bg-primary-hover"
              >
                Copy Secret
              </button>
              <button
                onClick={() => {
                  setShowSecretModal(false);
                  setNewSecret('');
                }}
                className="px-4 py-2 border rounded hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, variant: 'danger' })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.variant === 'danger' ? 'Delete' : 'Confirm'}
        variant={confirmDialog.variant}
      />
    </PageLayout>
  );
};

// Create Application Modal Component
const OIDC_TYPES = ['web', 'spa', 'native'];

const CreateApplicationModal = ({ onClose, onSuccess, onError }) => {
  const [formData, setFormData] = useState({
    name: '',
    label: '',
    description: '',
    app_type: 'saml',
    sign_on_url: '',
    redirect_uris: []
  });
  const [newRedirectUri, setNewRedirectUri] = useState('');
  const [loading, setLoading] = useState(false);
  const [createdApp, setCreatedApp] = useState(null);

  const isOidcType = OIDC_TYPES.includes(formData.app_type);

  const handleAddRedirectUri = () => {
    const uri = newRedirectUri.trim();
    if (!uri) return;
    if (formData.redirect_uris.includes(uri)) return;
    setFormData({ ...formData, redirect_uris: [...formData.redirect_uris, uri] });
    setNewRedirectUri('');
  };

  const handleRemoveRedirectUri = (uri) => {
    setFormData({ ...formData, redirect_uris: formData.redirect_uris.filter(u => u !== uri) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const cleanedData = {
        ...formData,
        name: formData.name.toLowerCase().replace(/\s+/g, '-')
      };

      // Only send redirect_uris for OIDC types
      if (!OIDC_TYPES.includes(cleanedData.app_type)) {
        delete cleanedData.redirect_uris;
      }

      const response = await applicationAPI.create(cleanedData);
      setCreatedApp(response.application);
    } catch (err) {
      onError(err.message || 'Failed to create application');
      setLoading(false);
    }
  };

  if (createdApp) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-screen overflow-y-auto">
          <h3 className="text-xl font-bold mb-4">✅ Application Created!</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Application Name</label>
              <div className="text-lg font-semibold">{createdApp.label}</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <p className="text-sm text-gray-900">{getAppTypeName(createdApp.app_type)}</p>
            </div>
          </div>

          <div className="mt-6 flex gap-2">
            <button
              onClick={onSuccess}
              className="flex-1 bg-primary text-white px-4 py-2 rounded hover:bg-primary-hover"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-screen overflow-y-auto">
        <h3 className="text-xl font-bold mb-4">Create New Application</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* App Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Application Type</label>
            <select
              value={formData.app_type}
              onChange={(e) => setFormData({ ...formData, app_type: e.target.value })}
              className="w-full border rounded px-3 py-2"
              required
            >
              <option value="saml">SAML 2.0</option>
              <option value="web">OIDC - Web Application</option>
              <option value="spa">OIDC - Single Page App</option>
              <option value="native">OIDC - Native/Mobile</option>
              <option value="service">OIDC - Service (M2M)</option>
              <option value="bookmark">Bookmark</option>
            </select>
          </div>

          {/* Name & Label */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name (ID)</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full border rounded px-3 py-2"
                placeholder="my-app"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Label</label>
              <input
                type="text"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                className="w-full border rounded px-3 py-2"
                placeholder="My Application"
                required
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full border rounded px-3 py-2"
              rows="2"
              placeholder="Brief description of your application"
            />
          </div>

          {/* Sign-on URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sign-on URL</label>
            <input
              type="url"
              value={formData.sign_on_url}
              onChange={(e) => setFormData({ ...formData, sign_on_url: e.target.value })}
              className="w-full border rounded px-3 py-2"
              placeholder="https://myapp.com"
            />
          </div>

          {/* Redirect URIs — OIDC only */}
          {isOidcType && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Redirect URIs <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-gray-500 mb-2">Where the IDP sends the user after authentication (RFC 6749 §3.1.2.2).</p>

              {formData.redirect_uris.length > 0 && (
                <div className="space-y-2 mb-2">
                  {formData.redirect_uris.map((uri, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <code className="flex-1 px-3 py-2 bg-gray-50 border rounded text-sm">{uri}</code>
                      <button type="button" onClick={() => handleRemoveRedirectUri(uri)} className="text-red-500 hover:text-red-700 px-2">✕</button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <input
                  type="url"
                  value={newRedirectUri}
                  onChange={(e) => setNewRedirectUri(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddRedirectUri())}
                  className="flex-1 border rounded px-3 py-2"
                  placeholder="https://myapp.com/callback"
                />
                <button type="button" onClick={handleAddRedirectUri} className="px-4 py-2 bg-gray-100 border rounded hover:bg-gray-200">Add</button>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-primary text-white px-4 py-2 rounded hover:bg-primary-hover disabled:bg-gray-400"
            >
              {loading ? 'Creating...' : 'Create Application'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// App Details Modal (simplified)
const AppDetailsModal = ({ app, onClose, onUpdate }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-screen overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold">{app.label}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Type</label>
            <p>{app.app_type}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Client ID</label>
            <code className="block bg-gray-50 p-2 rounded text-sm">{app.client_id}</code>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Scopes</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {app.scopes.map(scope => (
                <span key={scope} className="bg-blue-50 text-primary px-2 py-1 rounded text-sm">
                  {scope}
                </span>
              ))}
            </div>
          </div>
          {app.redirect_uris && app.redirect_uris.length > 0 && (
            <div>
              <label className="text-sm font-medium text-gray-700">Redirect URIs</label>
              <ul className="list-disc list-inside text-sm">
                {app.redirect_uris.map((uri, i) => (
                  <li key={i}>{uri}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <button
          onClick={onClose}
          className="mt-6 w-full bg-gray-100 text-gray-700 px-4 py-2 rounded hover:bg-gray-200"
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default ApplicationManager;
